import { getErrorMessage } from '@fruitful/fruitils';
import { AnalyticsUserEvent } from '@fruitful/types';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { PdfSheet, useToastController } from 'smoothie';

import { captureException, track } from '../../../logger/Logger';
import {
    buildAccountVerificationLetterFilename,
    downloadAccountVerificationLetter,
} from '../../api/AccountVerificationLetterApi';
import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import { AccountVerificationLetterSheet } from './AccountVerificationLetterSheet';
import type { AccountVerificationLetterFlowOptions } from './types';

export const useAccountVerificationLetterFlow = ({
    variant,
    defaultBankAccountId,
}: AccountVerificationLetterFlowOptions) => {
    const toast = useToastController();
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [pdfPreview, setPdfPreview] = useState<{
        uri: string;
        fileName: string;
    } | null>(null);
    const [isPdfSheetOpen, setIsPdfSheetOpen] = useState(false);

    const openSheet = useCallback(() => {
        setIsSheetOpen(true);
    }, []);

    const closeSheet = useCallback(() => {
        setIsSheetOpen(false);
    }, []);

    const handleDownload = useCallback(
        async (account: BankAccountDisplayDetail) => {
            setIsDownloading(true);
            try {
                const uri = await downloadAccountVerificationLetter({
                    variant,
                    bankAccountId: account.bankAccountId,
                    accountNumberMasked: account.accountNumberMasked,
                });

                const fileName = buildAccountVerificationLetterFilename({
                    variant,
                    accountNumberMasked: account.accountNumberMasked,
                });

                setIsSheetOpen(false);
                setPdfPreview({ uri, fileName });
                setIsPdfSheetOpen(true);
            } catch (error) {
                captureException(error);
                track(
                    AnalyticsUserEvent.ACCOUNT_VERIFICATION_LETTER_DOWNLOAD_ERROR,
                    {
                        bankAccountId: account.bankAccountId,
                        errorMessage: getErrorMessage(error),
                    },
                );
                toast.show('Something went wrong');
            } finally {
                setIsDownloading(false);
            }
        },
        [variant, toast],
    );

    const flowElements = (
        <>
            <AccountVerificationLetterSheet
                isOpen={isSheetOpen}
                onClose={closeSheet}
                defaultBankAccountId={defaultBankAccountId}
                isDownloading={isDownloading}
                onDownload={handleDownload}
            />
            {pdfPreview != null && (
                <PdfSheet
                    fileUri={pdfPreview.uri}
                    fileName={pdfPreview.fileName}
                    isOpen={isPdfSheetOpen}
                    onClose={() => {
                        setIsPdfSheetOpen(false);
                    }}
                />
            )}
        </>
    );

    return {
        openSheet,
        flowElements,
    };
};
